"use client";

import type { ChatCitation } from "@atlas/ai";
import { SourceChip } from "./SourceChip";

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

/** Content for a single narrative section: body text and optional citations. */
export interface NarrativeBlockData {
  /** Narrative body text. */
  body: string;
  /** Optional source citations rendered as SourceChip tags below the body. */
  sources?: ChatCitation[];
}

export interface NarrativeBlockProps {
  /** Section heading rendered as an h2. */
  title: string;
  /** Block content: body text and optional source citations. */
  block: NarrativeBlockData;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Document-style narrative section used in executive summaries and reports.
 *
 * Renders a bold heading, a readable body paragraph, and — when citations are
 * present — a compact "SOURCES" chip row below the body.
 *
 * When multiple NarrativeBlocks are stacked inside a flex-col container, the
 * `first:` variants suppress the top padding and top border on the first block
 * so consecutive blocks are cleanly separated by a hairline divider without an
 * orphaned gap at the top.
 */
export function NarrativeBlock({ title, block }: NarrativeBlockProps) {
  return (
    <section className="py-[22px] border-t border-(--border-subtle) first:pt-0 first:border-t-0">
      <h2 className="m-0 mb-[10px] text-[13px] font-semibold leading-[1.3] tracking-[-0.005em] text-(--fg-primary)">
        {title}
      </h2>
      <p className="m-0 text-[14px] leading-[1.65] text-(--fg-primary)">
        {block.body}
      </p>
      {block.sources && block.sources.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-(--fg-muted)">
            Sources
          </span>
          {block.sources.map((cite, i) => (
            <SourceChip
              key={`${cite.documentTitle}-${i}`}
              cite={cite}
              displayNum={String(i + 1)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
